import { useState, useEffect, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { Flag, ExternalLink, AlertTriangle, Headphones } from 'lucide-react';
import type { LiveCallResponse, LiveCallListenResponse } from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useFlagInteraction, useUnflagInteraction } from '@/hooks/use-interactions';
import { useListenToCall } from '@/hooks/use-listen-to-call';
import { ListenInPanel } from './ListenInPanel';
import { directionLabel, reasonLabel, toneStyle } from './format';

interface LiveCallRailProps {
  call: LiveCallResponse;
  /**
   * Called when a listen starts, so the page can pin its selection to this
   * call. Without it, an implicit selection follows the top of the
   * worst-first roster, and an unrelated call escalating would retarget the
   * rail and cut off the audio mid-sentence.
   */
  onListenStart?: (interactionId: string) => void;
}

/**
 * What the app knows about the selected call, beside the transcript.
 *
 * Everything here is read off the call row or derived from its tool
 * calls. There is deliberately no "held slot" or "identity check passed"
 * panel: the runtime does not report either, and a monitoring view that
 * displays a plausible guess is worse than one that stays quiet.
 */
export function LiveCallRail({ call, onListenStart }: LiveCallRailProps): React.ReactElement {
  const flag = useFlagInteraction();
  const unflag = useUnflagInteraction();
  const listen = useListenToCall();
  const [session, setSession] = useState<LiveCallListenResponse | null>(null);
  // The call the rail is showing RIGHT NOW, readable from inside an async
  // callback. A listen request's `onSuccess` closes over the render it was
  // made in; this ref is what it checks against when the response lands.
  const currentCallId = useRef(call.id);
  currentCallId.current = call.id;

  // Stop listening when the selection moves to a different call. Without
  // this the rail would keep the previous call's room connected while
  // showing the new call's details — a supervisor would be hearing one
  // patient while reading another's.
  useEffect(() => {
    setSession(null);
    listen.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset is stable; re-running on it would loop
  }, [call.id]);
  const reason = reasonLabel(call.reason);
  const isFlagPending = flag.isPending || unflag.isPending;

  return (
    <aside className="flex w-[312px] flex-shrink-0 flex-col overflow-y-auto border-l bg-sidebar">
      <section className="flex flex-col gap-3 p-4">
        <h2 className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">
          Where the call is
        </h2>

        <StageBar call={call} />

        <p className="text-[11px] leading-snug text-muted-foreground">
          Read from the tool calls this call has made — it shows what the agent has done, so it can
          trail the conversation by a step.
        </p>
      </section>

      <section className="flex flex-col gap-2.5 border-t p-4">
        <h2 className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">
          This call
        </h2>
        <Fact label="Direction" value={directionLabel(call.direction)} />
        <Fact label="Reason" value={reason ?? 'Not recorded'} />
        <Fact label="Number" value={call.phoneNumber ?? '—'} mono />
        <Fact label="Agent" value={call.agentName ?? '—'} />
        {call.direction === 'outbound' ? (
          <Fact label="Attempt" value={String(Math.max(1, call.attemptCount))} />
        ) : null}
        <Fact
          label="Answered"
          value={
            call.answeredAt
              ? new Date(call.answeredAt).toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : 'Still ringing'
          }
        />
        {call.booked ? <Fact label="Outcome" value="Appointment booked" tone="success" /> : null}
      </section>

      {call.escalated ? (
        <section className="border-t p-4">
          <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-2.5">
            <AlertTriangle className="mt-px h-3.5 w-3.5 flex-shrink-0 text-destructive" />
            <span className="text-xs leading-snug text-destructive">
              This call has been escalated to a person.
            </span>
          </div>
        </section>
      ) : null}

      <span className="flex-grow" />

      <section className="flex flex-col gap-2 border-t p-3">
        {session ? (
          <ListenInPanel session={session} onStop={() => setSession(null)} />
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="justify-start"
            disabled={!call.connected || listen.isPending}
            title={
              call.connected ? 'Hear this call. You will not be heard.' : 'Nobody has picked up yet'
            }
            onClick={() => {
              const requestedId = call.id;
              onListenStart?.(requestedId);
              listen.mutate(requestedId, {
                onSuccess: (result) => {
                  // DROP A RESPONSE FOR A CALL WE ARE NO LONGER SHOWING.
                  // `listen.reset()` in the effect above does not cancel an
                  // in-flight request, and the rail does not unmount on a
                  // switch — so without this check a slow response for call
                  // A connects the supervisor to A's room while every
                  // detail on screen belongs to B.
                  if (currentCallId.current !== requestedId) return;
                  setSession(result);
                },
              });
            }}
          >
            <Headphones className="h-3.5 w-3.5" />
            {listen.isPending ? 'Connecting…' : 'Listen in'}
          </Button>
        )}

        <Button asChild variant="outline" size="sm" className="justify-start">
          <Link to="/activity/$interactionId" params={{ interactionId: call.id }}>
            <ExternalLink className="h-3.5 w-3.5" />
            Open full call record
          </Link>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={cn('justify-start', call.flagged && 'text-destructive')}
          disabled={isFlagPending}
          onClick={() => (call.flagged ? unflag.mutate(call.id) : flag.mutate(call.id))}
        >
          <Flag className={cn('h-3.5 w-3.5', call.flagged && 'fill-current')} />
          {call.flagged ? 'Remove flag' : 'Flag this call'}
        </Button>
      </section>
    </aside>
  );
}

function StageBar({ call }: { call: LiveCallResponse }): React.ReactElement {
  const tone = toneStyle(call.tone);
  const segments = Array.from({ length: call.stage.total }, (_, i) => i + 1);

  return (
    <div>
      <div
        className="flex gap-1"
        role="img"
        aria-label={`${call.stage.label}, step ${call.stage.index} of ${call.stage.total}`}
      >
        {segments.map((position) => {
          const reached = position <= call.stage.index;
          const isCurrent = position === call.stage.index;

          return (
            <span
              key={position}
              className={cn(
                'h-1.5 flex-grow rounded-full',
                reached ? (call.tone === 'normal' ? 'bg-primary' : tone.dot) : 'bg-border',
                isCurrent && call.connected && 'animate-pulse'
              )}
            />
          );
        })}
      </div>
      <p className="mt-2 text-sm font-semibold">{call.stage.label}</p>
      <p className="text-[11px] text-muted-foreground">
        Step {call.stage.index} of {call.stage.total}
      </p>
    </div>
  );
}

function Fact({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: 'success';
}): React.ReactElement {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[12.5px]">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={cn(
          'truncate font-medium',
          mono && 'font-mono text-[12px]',
          tone === 'success' && 'text-success'
        )}
      >
        {value}
      </span>
    </div>
  );
}
